import { useState, useEffect, useRef } from 'react';
import { useGetDocuments } from './useDocumentClient';

let sharedStatsPromise = null;
let cachedStats = null;
let cacheTimestamp = null;
let activeStatsMounts = 0;
let cacheInvalidationCounter = 0;
let refreshCallbacks = new Set();
const CACHE_DURATION = 30000;

export const invalidateDocumentStatsCache = () => {
  cachedStats = null;
  cacheTimestamp = null;
  sharedStatsPromise = null;
  cacheInvalidationCounter++;
  refreshCallbacks.forEach(callback => {
    try {
      callback();
    } catch (error) {
    }
  });
};

export const useDocumentStats = () => {
  const getDocuments = useGetDocuments();
  const [stats, setStats] = useState({ completed: 0, total: 0, isLoading: true });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const isMountedRef = useRef(true);
  const getDocumentsRef = useRef(getDocuments);
  const lastInvalidationCounterRef = useRef(cacheInvalidationCounter);

  useEffect(() => {
    getDocumentsRef.current = getDocuments;
  }, [getDocuments]);

  useEffect(() => {
    const refreshCallback = () => {
      if (isMountedRef.current) {
        setRefreshTrigger(prev => prev + 1);
      }
    };
    refreshCallbacks.add(refreshCallback);
    return () => {
      refreshCallbacks.delete(refreshCallback);
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    activeStatsMounts++;

    const fetchStats = async () => {
      const wasInvalidated = lastInvalidationCounterRef.current !== cacheInvalidationCounter;
      if (wasInvalidated) {
        lastInvalidationCounterRef.current = cacheInvalidationCounter;
      } else {
        const now = Date.now();
        if (cachedStats && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
          if (isMountedRef.current) {
            setStats({ ...cachedStats, isLoading: false });
          }
          return cachedStats;
        }
      }

      if (sharedStatsPromise) {
        try {
          const result = await sharedStatsPromise;
          if (isMountedRef.current && result) {
            cachedStats = result;
            cacheTimestamp = Date.now();
            setStats({ ...result, isLoading: false });
          }
          return result;
        } catch (error) {
          if (isMountedRef.current) {
            setStats({ completed: 0, total: 0, isLoading: false });
          }
          return { completed: 0, total: 0 };
        }
      }

      try {
        if (isMountedRef.current) {
          setStats(prev => ({ ...prev, isLoading: true }));
        }
        const data = await getDocumentsRef.current({}, {}, {});

        if (!isMountedRef.current) return { completed: 0, total: 0 };

        let result = { completed: 0, total: 0 };

        if (data && data.document_list) {
          const total = data.document_list.length;
          const completed = data.document_list.filter(
            (doc) => doc?.sign_detail?.is_signed === true
          ).length;
          result = { completed, total };
        }

        cachedStats = result;
        cacheTimestamp = Date.now();

        if (isMountedRef.current) {
          setStats({ ...result, isLoading: false });
        }

        return result;
      } catch (error) {
        if (isMountedRef.current) {
          setStats({ completed: 0, total: 0, isLoading: false });
        }
        throw error;
      }
    };

    sharedStatsPromise = fetchStats()
      .then((result) => {
        sharedStatsPromise = null;
        return result;
      })
      .catch((error) => {
        sharedStatsPromise = null;
        throw error;
      });

    return () => {
      isMountedRef.current = false;
      activeStatsMounts--;
      setTimeout(() => {
        if (activeStatsMounts === 0) {
          sharedStatsPromise = null;
        }
      }, 100);
    };
  }, [
    refreshTrigger
  ]);

  return stats;
};
